import { debounce } from 'lodash';
import { useMessage, genMessageId } from './useMessage';
import { IntentType, IntentTypeConfig, Message } from '../dataType';
import http from 'common-module/src/util/http';
import { computed } from '@vue/composition-api';
import { useLocale } from './useLocale';
import { useConfig } from './useConfig';
import transformContent from '../util/transformContent';

//长时间停留卡片时间 目前为5分钟 300000
const INTENTION_BACK_TIME = ['prod', 'pre'].includes((window as any).$config.VUE_APP_ENV) ? 300000 : 20000;

export const useIntention = () => {
  const { getUserInfo } = useConfig();
  const { messages, addRobotMessage, lastResponseMessage, subscribe, deleteMessage, updateRobotWaitMessage } = useMessage();
  const { t } = useLocale();
  const { userName, dataQueriesSwitch } = getUserInfo();
  const intentionTab = computed<IntentTypeConfig[]>(() =>{
    return [
      {
        label: t('saas.robotChat.Intention.assistant'),
        value: 'knowledge_qa',
        icon: 'iconQAAssistant',
        enabled: true
      },
      {
        label: t('saas.robotChat.Intention.Report'),
        value: 'work_report',
        icon: 'iconbaogao',
        enabled: true
      },
      {
        label: t('saas.robotChat.intent.progress'),
        value: 'progress',
        icon: 'iconjindutiao',
        enabled: true
      },
      {
        label: t('saas.robotChat.Intention.dataQueries'),
        value: 'sql_query',
        // eslint-disable-next-line spellcheck/spell-checker
        icon: 'iconicon_chakan',
        enabled: dataQueriesSwitch
      },
      {
        label: t('saas.robotChat.Intention.Risk'),
        value: 'risk',
        icon: 'iconfengxianguanli1',
        enabled: false
      }
    ].filter(r => r.enabled) as IntentTypeConfig[];
  });

  /**
   * 初始化发送意图
   */
  const initIntention = async () => {
    const res = await http.post('/chat/example', { type:'first_chat' });
    const examples = res?.example?.examples || [];
    addRobotMessage({
      id: genMessageId(),
      type: 'ai.intention',
      content: {
        greetText:t('saas.rootChat.intention.greetText', { name:userName }),
        mainText: t('saas.rootChat.intention.mainText'),
        examples: examples
      },
      createTime: new Date().getTime()
    });
  };
  /**
   * 长时间未操作发送二次意图
   */
  const noOperationDebounce = debounce(() => {
    noOperationRobot();
  }, INTENTION_BACK_TIME, { leading: false, trailing: true });
  // 60*1000*5
    /**
   * 发送二次意图
   */
  const noOperationRobot = async (isEnter: boolean = false) => {
    if (!['ai.intentionBack', 'ai.intention', 'ai.continueResult', 'ai.waiting'].includes(lastResponseMessage.value.type)) {
      const type = isEnter ? 'long_pause' : 'ask_result';
      const res = await http.post('/chat/example', { type: type });
      // 意图有结果追问
      if (res.type === 'ask_result') {
        sendContinueMessage(res);
        return;
      }
      addRobotMessage({
        id: genMessageId(),
        type: 'ai.intentionBack',
        content: {
          greetText:t('saas.rootChat.intentionBack.greetText'),
          mainText: t('saas.rootChat.intentionBack.mainText'),
          examples: res?.example?.examples || []
        },
        createTime: new Date().getTime()
      });
    }
  };
    /**
   * 清楚正在计算等待的意图推送
   */
  const clearIntentionBack = () => {
    noOperationDebounce?.cancel();
  };
    /**
   * 发送二次意图消息
   */
  const intentionBackSendMessage = () => {
    // 如果最后一条消息不是二次意图消息或者意图消息
    if (!['ai.intentionBack', 'ai.intention'].includes(lastResponseMessage.value.type)) {
      clearIntentionBack();
      noOperationRobot(true);
    }
  };
  /**
   * 监听消息
   */
  const subscribeForIntentBack = () => subscribe((message: Message<any> | Message<any>[]) => {
    // 多条消息不处理
    if (Array.isArray(message)) {
      return;
    }
    if (!['ai.intentionBack', 'ai.intention', 'ai.waiting', 'custom'].includes(message.type)) {
      clearIntentionBack();
      noOperationDebounce();
    }
    // 清除意向的新消息推送  点击问题和输入都会出发ai.sendText
    if (message.type === 'ai.sendText') {
      // 清除自定义新消息
      const customIntention = messages.value.findLast(message => message.content?.type === 'change.intention');
      customIntention && deleteMessage(customIntention.id);
      // 清除二次消息卡片
      const intentionBackMessage = messages.value.findLast(message => message.type === 'ai.intentionBack');
      intentionBackMessage && deleteMessage(intentionBackMessage.id);
      // 清楚首次意图消息
      // 如果消息条数少，则需要判断是否存在意图消息清除，如果多了则不需要因为必然不是初始进入
      if (messages.value.length < 5) {
        const intentionMessage = messages.value.find(message => message.type === 'ai.intention');
        intentionMessage && deleteMessage(intentionMessage.id);
      }
    }

  });

  /**
   * 识别手动推荐意图函数
   */
  const verifySendIntention = (text: string) => {
    const includesText = ['?', '？', 'help'];
    return includesText.includes(text.toLocaleLowerCase());
  };

  /**
   * 发送意图问题或追问卡片
   */
  const sendIntentionRecommend = async (sendText:string, intent: IntentType, queryQuest: boolean) => {
    const res = await http.post('/chat/example', { type: 'help', text: sendText, intent: intent, queryQuest : queryQuest, header: t('saas.rootChat.intentionBack.helpTitle') });
    res.type === 'ask_result' && sendContinueMessage(res);
  };

  /**
   * 发送追问卡片
   */
  const sendContinueMessage = (result: {askResult: any }) => {
    const data = transformContent['ai.receiveRecommendData']?.({ ...result.askResult, recommendType: 'continue' }) || {
      content: null,
      title: t('saas.rootChat.intention.continueAsk')
    };
    updateRobotWaitMessage({
      id: genMessageId(),
      type: 'ai.continueResult',
      content: data.content,
      title: t('saas.rootChat.intention.continueAsk')
    });
  };

  return {
    intentionTab,
    initIntention,
    noOperationDebounce,
    noOperationRobot,
    clearIntentionBack,
    intentionBackSendMessage,
    subscribeForIntentBack,

    verifySendIntention,
    sendIntentionRecommend
  };
};
